import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';

import { FlatTreeControl } from '@angular/cdk/tree';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { CourseCatalogLandingPageApiService } from 'app/library/course-catalog-landing-page/services/course-catalog-landing-page.api-service';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { CreateValidScheduledStreamContentBulkSuccess } from 'app/library/scheduled-stream-content/actions';
import { IScheduleStreamContentBulkActionPayload } from 'app/library/scheduled-stream-content/interfaces/schedule-stream-content-bulk.action-payload.interface';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IEngageCategoryFilters } from '../../interfaces/engage-category-filters.interface';
import { IPostingItemPicked } from '../../interfaces/posting-item-picked.interface';
import { EngageCategory } from '../../models/engage-category.model';
import { PostingType } from '../../models/posting-type.model';
import { PostingTypeApiService } from '../../services/posting-type.api-service';
import { PostingService } from '../../services/posting.service';

interface ICourseStructureCategoryFlatNode {
    expandable: boolean;
    id: string;
    name: string;
    type: string;
    numberOfResults: number;
    level: number;
    offerringGroupOfferings?: CourseCatalogPostingStream[];
    isLoading: boolean;
    isFetched: boolean;
}

@Component({
    selector: 'app-engage-category-tree-page',
    templateUrl: './engage-category-tree-page.component.html',
    styleUrls: ['./engage-category-tree-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class EngageCategoryTreeComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    selectedContentTypeId: string;

    @Input()
    pickOnly = false;

    @Input()
    categoryFilters: IEngageCategoryFilters = {};

    @Output()
    closeDialog = new EventEmitter<boolean>();

    @Output()
    itemPicked = new EventEmitter<IPostingItemPicked>();

    searchInput = new FormControl('');
    fetchCourseStructureCategoriesDataAsTree = false;
    selectedPostingStreams: CourseCatalogPostingStream[] = [];
    isLoading: boolean;
    postingTypeInput = new FormControl();
    postingTypes: PostingType[];

    private _transformer = (node: EngageCategory, level: number) => {
        return {
            expandable: !!node.children && node.children.length > 0,
            id: node.category.id,
            name: node.category.name,
            type: node.category.type,
            numberOfResults: node.numberOfResults,
            level: level,
        };
    };

    treeControl = new FlatTreeControl<ICourseStructureCategoryFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _postingService: PostingService,
        private _courseCatalogApiService: CourseCatalogLandingPageApiService,
        private _featuredStreamContentService: ScheduledStreamContentService,
        private _postingTypeApiService: PostingTypeApiService,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.postingTypes = await this._postingTypeApiService.getPostingTypes('course_offering');

        this.postingTypeInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._loadTreeData();
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(500), distinctUntilChanged()).subscribe(() => {
            this._loadTreeData();
        });

        this._loadTreeData();

        this._action$
            .pipe(ofActionDispatched(CreateValidScheduledStreamContentBulkSuccess), takeUntil(this._unsubscribeAll))
            .subscribe((action: CreateValidScheduledStreamContentBulkSuccess) => {
                const bulk = action.payload;

                const createdCount = bulk.created.length;
                const failedCount = bulk.failed.length;

                let message: string;

                if (createdCount > 0) {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_SUCCESS', {
                        createdCount,
                        failedCount,
                    });
                } else {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED');
                }

                const errorTranslationMap: Record<string, string> = {
                    time_interval_overlaps_with_another_ScheduledStreamContent_interval: 'ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED.ERROR',
                };

                const groupedErrors = bulk.failed.reduce((acc, item) => {
                    const msg = item.message;

                    if (!acc[msg]) {
                        acc[msg] = 0;
                    }
                    acc[msg] += 1;

                    return acc;
                }, {} as Record<string, number>);

                const errorLines = Object.entries(groupedErrors)
                    .map(([msg, count]) => `${this._translateService.instant(errorTranslationMap[msg] || msg)} (${count} items)`)
                    .join('\n');

                if (failedCount > 0) {
                    message += `\n\n${errorLines}`;
                }

                this._matSnackBar.open(message, 'OK', {
                    verticalPosition: 'top',
                    duration: 5000,
                });

                this.isLoading = false;
                this.closeDialog.emit();
            });
    }

    private async _loadTreeData(): Promise<void> {
        const searchTerm = this.searchInput.value;
        const postingType = this.postingTypeInput.value;
        const categories = await this._postingService.fetchEngageCategories(this.audienceId, searchTerm, postingType, this.categoryFilters);

        this.dataSource.data = categories || [];

        this.isLoading = false;
    }

    hasChild = (_: number, node: ICourseStructureCategoryFlatNode) => node.expandable;

    isNodeExpandable(node: ICourseStructureCategoryFlatNode): boolean {
        // If subcategories, expandable only if children exist
        if (node.type === 'sub_categories') {
            return node.expandable;
        }

        // If offerings, expandable only if there are results
        if (node.type === 'offerings') {
            return node.numberOfResults > 0;
        }

        return false;
    }

    expandNode(node: ICourseStructureCategoryFlatNode, event: MouseEvent): void {
        if (!this.isNodeExpandable(node)) {
            event.stopPropagation();
            return;
        }

        this.fetchCourseOfferingsByCategory(node);
    }

    navigateToChangeExperience(): void {
        this._store.dispatch(new Navigate([`/admin/grow/audiences/${this.audienceId}/relevant-experiences`]));

        this.closeDialog.emit(true);
    }

    async fetchCourseOfferingsByCategory(categoryItem: ICourseStructureCategoryFlatNode): Promise<void> {
        if (categoryItem.type === 'offerings' && !categoryItem.isFetched && categoryItem.numberOfResults && this.treeControl.isExpanded(categoryItem)) {
            categoryItem.isLoading = true;

            try {
                const searchTerm = this.searchInput.value || undefined;
                const postingType = this.postingTypeInput.value || undefined;

                categoryItem.offerringGroupOfferings = await this._courseCatalogApiService
                    .getCategoryPostings(categoryItem.id, 100, searchTerm, postingType)
                    .toPromise();
                categoryItem.isFetched = true;
            } finally {
                categoryItem.isLoading = false;
            }
        }
    }

    onSelectPostingStream(postingStream: CourseCatalogPostingStream): void {
        if (this.pickOnly && this.selectedPostingStreams.length) {
            postingStream.isSelected = false;
            return;
        }

        this.selectedPostingStreams = [...this.selectedPostingStreams, postingStream];
    }

    onDeselectPostingStream(postingStream: CourseCatalogPostingStream): void {
        postingStream.isSelected = false;
        this.selectedPostingStreams = this.selectedPostingStreams.filter((p) => p.id !== postingStream.id);
    }

    removeFromList(posting: CourseCatalogPostingStream): void {
        this.onDeselectPostingStream(posting);
    }

    onClearSelection(): void {
        this.selectedPostingStreams.forEach((postingStream) => {
            postingStream.isSelected = false;
        });
        this.selectedPostingStreams = [];
    }

    save(): void {
        this.isLoading = true;

        if (this.pickOnly) {
            const first = this.selectedPostingStreams[0];
            const translation = first?.translation;

            if (first) {
                this.itemPicked.emit({
                    postingId: first.id ? Number(first.id) : null,
                    url: first.url || null,
                    title: translation?.title || '',
                    description: translation?.description || '',
                    image: first.image ?? null,
                });
            }

            this.isLoading = false;
            return;
        }

        const data: IScheduleStreamContentBulkActionPayload = {
            audience: { id: this.audienceId },
            featuredStream: { id: this.featuredStreamContentId },
            postingIds: this.selectedPostingStreams.map((postingStream) => Number(postingStream.id)),
        };

        this._featuredStreamContentService.createScheduledStreamContentBulk(data);
    }
}
